import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseUrl = process.env.REACT_APP_API_URL || "https://api.naturalremediesapp.com/api";


const isTokenExpired = (tokenExpiry) => {
  if (!tokenExpiry) return true;
  return new Date(tokenExpiry) <= new Date();
};

const refreshToken = async () => {
  const refreshToken = localStorage.getItem("admin_refresh_token");
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    const response = await fetch(`${baseUrl}/auth/admin/refresh?refresh_token=${refreshToken}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (data.success && data.access_token) {
      localStorage.setItem("admin_token", data.access_token);
      localStorage.setItem("admin_refresh_token", data.refresh_token);
      localStorage.setItem("admin_token_expires_at", data.access_token_expires_at);
      localStorage.setItem("admin_refresh_token_expires_at", data.refresh_token_expires_at);
      return data.access_token;
    } else {
      throw new Error("Failed to refresh token");
    }
  } catch (error) {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_refresh_token");
    localStorage.removeItem("admin_token_expires_at");
    localStorage.removeItem("admin_refresh_token_expires_at");
    localStorage.removeItem("admin_data");
    throw error;
  }
};

export const subscriptionsApi = createApi({
  reducerPath: "subscriptionsApi",
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers) => {
      const token = localStorage.getItem("admin_token");
      const tokenExpiry = localStorage.getItem("admin_token_expires_at");

      if (token && isTokenExpired(tokenExpiry)) {
        try {
          const newToken = await refreshToken();
          headers.set("Authorization", `Bearer ${newToken}`);
        } catch (error) {
          window.location.href = "/admin/auth/sign-in";
          throw error;
        }
      } else if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
  }),
  tagTypes: ['Subscription'],
  endpoints: (builder) => ({
    // Get all subscriptions with optional search and pagination
    getSubscriptions: builder.query({
      query: (searchParams = {}) => {
        const params = new URLSearchParams();
        if (searchParams.page) {
          params.append("page", String(searchParams.page));
        }
        if (searchParams.per_page) {
          params.append("per_page", String(searchParams.per_page));
        }
        if (searchParams.search) {
          params.append("search", searchParams.search);
        }
        if (searchParams.status) {
          params.append("status", searchParams.status);
        }
        if (searchParams.plan_id) {
          params.append("plan_id", String(searchParams.plan_id));
        }
        const queryString = params.toString();
        return {
          url: `/subscriptions${queryString ? `?${queryString}` : ""}`,
        };
      },
      transformResponse: (response) => {
        // Return the full response to access both data and pagination
        return response;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ user_id }) => ({ type: 'Subscription', id: user_id })),
              { type: 'Subscription', id: 'LIST' },
            ]
          : [{ type: 'Subscription', id: 'LIST' }],
    }),
    getSubscription: builder.query({
      query: (userId) => ({ url: `/subscriptions/${userId}` }),
      providesTags: (_result, _error, userId) => [{ type: 'Subscription', id: userId }],
    }),
    changeSubscription: builder.mutation({
      query: ({ userId, ...body }) => ({
        url: `/subscriptions/${userId}/change`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'Subscription', id: userId },
        { type: 'Subscription', id: 'LIST' },
      ],
    }),
    cancelPendingSubscription: builder.mutation({
      query: (userId) => ({
        url: `/subscriptions/${userId}/cancel-pending`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, userId) => [
        { type: 'Subscription', id: userId },
        { type: 'Subscription', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetSubscriptionsQuery,
  useGetSubscriptionQuery,
  useChangeSubscriptionMutation,
  useCancelPendingSubscriptionMutation,
} = subscriptionsApi;

export { refreshToken, isTokenExpired };
